import type { ApartmentFilterOptions } from '@services/livability/types'

import type { ApartmentFilters } from '../../utils/apartmentFilterUrl'

export interface ChipOption {
  value: string
  label: string
}

export interface RangeFilterDef {
  type: 'range'
  id: string
  label: string
  minKey: 'minPrice' | 'minArea' | 'minPricePerM2'
  maxKey: 'maxPrice' | 'maxArea' | 'maxPricePerM2'
  unit?: string
}

export interface SelectFilterDef {
  type: 'select'
  id: string
  label: string
  key: 'city' | 'district' | 'condition' | 'buildingType'
  getOptions: (
    filterOptions?: ApartmentFilterOptions,
    filters?: ApartmentFilters
  ) => ChipOption[]
  isAvailable?: (
    filters: ApartmentFilters,
    filterOptions?: ApartmentFilterOptions
  ) => boolean
}

export interface MultiButtonGroupFilterDef {
  type: 'multiButtonGroup'
  id: string
  label: string
  key: 'roomCounts'
  options: { value: number; label: string }[]
}

export interface NumberFilterDef {
  type: 'number'
  id: string
  label: string
  key: 'minIndex'
  min: number
  max: number
  placeholder: string
}

export interface BooleanFilterDef {
  type: 'boolean'
  id: string
  label: string
  key:
    | 'hasParking'
    | 'hasBalcony'
    | 'hasElevator'
    | 'petsAllowed'
    | 'furnished'
    | 'fullyFurnished'
    | 'availableNow'
    | 'nearCenter'
}

export type FilterDef =
  | RangeFilterDef
  | SelectFilterDef
  | MultiButtonGroupFilterDef
  | NumberFilterDef
  | BooleanFilterDef

const ROOM_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label: n === 5 ? '5+' : String(n)
}))

export function formatCityLabel(value: string): string {
  return value
    .split(/[_-]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function getDistrictsForCity(
  filterOptions: ApartmentFilterOptions | undefined,
  city: string | undefined
): string[] {
  if (!city || !filterOptions?.districts) return []
  const key = city.toLowerCase()
  return filterOptions.districts[key] ?? []
}

export const RANGE_DEFS: RangeFilterDef[] = [
  {
    type: 'range',
    id: 'price',
    label: 'Cena',
    minKey: 'minPrice',
    maxKey: 'maxPrice',
    unit: '€'
  },
  {
    type: 'range',
    id: 'pricePerM2',
    label: 'Cena za m²',
    minKey: 'minPricePerM2',
    maxKey: 'maxPricePerM2',
    unit: '€/m²'
  },
  {
    type: 'range',
    id: 'area',
    label: 'Plocha',
    minKey: 'minArea',
    maxKey: 'maxArea',
    unit: 'm²'
  }
]

export const MULTI_BUTTON_GROUP_DEFS: MultiButtonGroupFilterDef[] = [
  {
    type: 'multiButtonGroup',
    id: 'rooms',
    label: 'Izby',
    key: 'roomCounts',
    options: ROOM_OPTIONS
  }
]

export const SELECT_DEFS: SelectFilterDef[] = [
  {
    type: 'select',
    id: 'city',
    label: 'Mesto',
    key: 'city',
    getOptions: (filterOptions) =>
      (filterOptions?.cities ?? []).map((c) => ({
        value: c,
        label: formatCityLabel(c)
      }))
  },
  {
    type: 'select',
    id: 'district',
    label: 'Mestská časť',
    key: 'district',
    isAvailable: (filters) => Boolean(filters.city),
    getOptions: (filterOptions, filters) => {
      const city = filters?.city
      if (!city) return []
      return getDistrictsForCity(filterOptions, city).map((d) => ({
        value: d,
        label: formatCityLabel(d)
      }))
    }
  },
  {
    type: 'select',
    id: 'condition',
    label: 'Stav bytu',
    key: 'condition',
    getOptions: (filterOptions) =>
      (filterOptions?.conditions ?? []).map((c) => ({ value: c, label: c }))
  },
  {
    type: 'select',
    id: 'buildingType',
    label: 'Typ budovy',
    key: 'buildingType',
    getOptions: (filterOptions) =>
      (filterOptions?.building_types ?? []).map((t) => ({ value: t, label: t }))
  }
]

export const NUMBER_DEFS: NumberFilterDef[] = [
  {
    type: 'number',
    id: 'minIndex',
    label: 'Min. index',
    key: 'minIndex',
    min: 0,
    max: 100,
    placeholder: '0–100'
  }
]

export const BOOLEAN_DEFS: BooleanFilterDef[] = [
  { type: 'boolean', id: 'hasParking', label: 'Parkovanie', key: 'hasParking' },
  { type: 'boolean', id: 'hasBalcony', label: 'Balkón', key: 'hasBalcony' },
  { type: 'boolean', id: 'hasElevator', label: 'Výťah', key: 'hasElevator' },
  {
    type: 'boolean',
    id: 'petsAllowed',
    label: 'Zvieratá povolené',
    key: 'petsAllowed'
  },
  { type: 'boolean', id: 'furnished', label: 'Zariadený', key: 'furnished' },
  {
    type: 'boolean',
    id: 'fullyFurnished',
    label: 'Kompletne zariadený',
    key: 'fullyFurnished'
  },
  {
    type: 'boolean',
    id: 'availableNow',
    label: 'Dostupný ihneď',
    key: 'availableNow'
  },
  {
    type: 'boolean',
    id: 'nearCenter',
    label: 'Blízko centra',
    key: 'nearCenter'
  }
]

export const ALL_FILTER_DEFS: FilterDef[] = [
  ...RANGE_DEFS,
  ...MULTI_BUTTON_GROUP_DEFS,
  ...SELECT_DEFS,
  ...NUMBER_DEFS,
  ...BOOLEAN_DEFS
]

export const QUICK_FILTER_IDS = ['price', 'rooms', 'city'] as const

export function getFilterDefById(id: string): FilterDef | undefined {
  return ALL_FILTER_DEFS.find((d) => d.id === id)
}

export function isFilterDefOffered(
  def: FilterDef,
  filters: ApartmentFilters,
  filterOptions?: ApartmentFilterOptions
): boolean {
  if (def.type === 'select' && def.isAvailable) {
    return def.isAvailable(filters, filterOptions)
  }
  return true
}

export function isFilterDefActive(
  def: FilterDef,
  filters: ApartmentFilters
): boolean {
  switch (def.type) {
    case 'range':
      return (
        filters[def.minKey] !== undefined || filters[def.maxKey] !== undefined
      )
    case 'number':
      return filters[def.key] !== undefined
    case 'select':
      return Boolean(filters[def.key])
    case 'multiButtonGroup':
      return (filters.roomCounts?.length ?? 0) > 0
    case 'boolean':
      return filters[def.key] === true
  }
}

export function formatChipLabel(
  def: FilterDef,
  filters: ApartmentFilters,
  filterOptions?: ApartmentFilterOptions
): string {
  switch (def.type) {
    case 'range': {
      const min = filters[def.minKey]
      const max = filters[def.maxKey]
      const unit = def.unit ? ` ${def.unit}` : ''
      if (min !== undefined && max !== undefined)
        return `${def.label}: ${min}–${max}${unit}`
      if (min !== undefined) return `${def.label}: od ${min}${unit}`
      if (max !== undefined) return `${def.label}: do ${max}${unit}`
      return def.label
    }
    case 'number':
      return `${def.label}: ${filters[def.key] ?? ''}`
    case 'multiButtonGroup': {
      const selected = filters.roomCounts ?? []
      if (selected.length === 0) return def.label
      const labels = selected
        .map(
          (value) =>
            def.options.find((o) => o.value === value)?.label ?? String(value)
        )
        .join(', ')
      return `${def.label}: ${labels}`
    }
    case 'select': {
      const value = filters[def.key]
      if (!value) return def.label
      const options = def.getOptions(filterOptions, filters)
      return options.find((o) => o.value === value)?.label ?? value
    }
    case 'boolean':
      return def.label
  }
}

export function applyFilterPatch(
  filters: ApartmentFilters,
  patch: Partial<ApartmentFilters>
): ApartmentFilters {
  const next = { ...filters }
  for (const key of Object.keys(patch) as (keyof ApartmentFilters)[]) {
    const value = patch[key]
    if (value === undefined || (Array.isArray(value) && value.length === 0)) {
      delete next[key]
    } else {
      ;(next as Record<string, unknown>)[key] = value
    }
  }
  return next
}

export function clearFilterDef(
  def: FilterDef,
  filters: ApartmentFilters
): ApartmentFilters {
  const next = { ...filters }
  switch (def.type) {
    case 'range':
      delete next[def.minKey]
      delete next[def.maxKey]
      break
    case 'select':
      delete next[def.key]
      if (def.key === 'city') delete next.district
      break
    case 'multiButtonGroup':
      delete next.roomCounts
      break
    default:
      delete next[def.key]
  }
  return next
}
